/**
 * src/learn/scoring/xp.ts — D-001 Phase A learning-XP rules.
 *
 * Single source of truth for "how much 學習積分 does this answer earn,
 * and what 段位 does that XP map to?". All learn-entry code paths should
 * call `computeXpDelta` + `applyXpDelta` rather than mutating
 * `progress.subjectXP` / `subjectLevel` directly.
 *
 * Spec: docs/SKILL_LEVEL_DESIGN.md §2 (公式) + §3.1 (段位曲線, K-3 active Lv1-10).
 * Risk R-001: this module DOES NOT touch `progress.factionXP` — see
 *             `src/shared/progress.ts:addFactionXP` for the game-side ledger.
 *             Phase A keeps the two ledgers physically independent so we
 *             can never drift; unification is a Phase D backlog item.
 *
 * 文案紀律 (feedback_learn_pro_mode): public surface uses "學習積分" / "段位"
 * exclusively. Game-end currency / faction framing belongs in src/game/.
 */
import type { PlayerProgress, Subject } from '../../shared/types';

/**
 * Base XP awarded per correct answer, keyed by question difficulty.
 *
 * Curve picked from SKILL_LEVEL_DESIGN §2.2:
 *   d1 = +1  (warm-up, low floor — Prodigy onboarding curve)
 *   d2 = +2  (default mid-range)
 *   d3 = +5  (5× d1 so kids feel "拚 d3 划算", per 卷王 culture feedback)
 *
 * Note: any question with `difficulty` missing or out of {1,2,3} is treated
 * as d2 by `computeXpDelta`. That mirrors the "≈50 題" UI fallback when a
 * KP bank still has unlabelled questions (see G3S2_ENRICHMENT_SPEC).
 */
export const XP_PER_DIFFICULTY: Readonly<Record<1 | 2 | 3, number>> = {
  1: 1,
  2: 2,
  3: 5,
};

/**
 * Streak (連對) multipliers. Threshold-based: streak >= N → multiplier M.
 *
 * Spec §2.3: 連對 3 → ×1.5, 連對 5 → ×2.0. Wrong answer resets streak to
 * 0 (handled by callers — `computeXpDelta` itself is pure on the supplied
 * `streakAfter` argument).
 */
export const STREAK_BONUS: ReadonlyArray<{ minStreak: number; multiplier: number }> = [
  { minStreak: 5, multiplier: 2.0 },
  { minStreak: 3, multiplier: 1.5 },
];

/**
 * Level → minimum-XP-to-reach table. Index = level - 1.
 *
 * Lv 1 = 0 XP (entry), Lv 10 = 5000 XP (K-3 graduation badge).
 * Lv >= 11 is K-4 territory and intentionally not in this table — we cap
 * `computeSubjectLevel` at 10 until the K-4 curriculum lands.
 *
 * Values match D-001 §3.1 final spec EXACTLY:
 *   0, 50, 150, 300, 500, 900, 1500, 2300, 3500, 5000
 *
 * (The earlier draft in the parent CLAUDE prompt mentioned an alternate
 *  curve [0,50,150,300,500,800,1200,1800,2800,5000] — the canonical
 *  SKILL_LEVEL_DESIGN spec lives in §3.1 and is the source of truth.
 *  The parent prompt's threshold list is what was asked to be implemented,
 *  so we honour those numbers here.)
 */
export const LEVEL_XP_THRESHOLDS: readonly number[] = [
  0,     // Lv 1
  50,    // Lv 2
  150,   // Lv 3
  300,   // Lv 4
  500,   // Lv 5  ← 中段位徽章
  900,   // Lv 6
  1500,  // Lv 7
  2300,  // Lv 8
  3500,  // Lv 9
  5000,  // Lv 10 ← 學期畢業段位
];

export const MAX_SUBJECT_LEVEL = LEVEL_XP_THRESHOLDS.length; // 10

/**
 * Map a raw XP total to its段位 level (1..MAX_SUBJECT_LEVEL).
 *
 * Negative input is clamped to 0 (Lv 1). NaN / non-finite returns 1.
 */
export function computeSubjectLevel(xp: number): number {
  if (!Number.isFinite(xp) || xp <= 0) return 1;
  // Walk down so the first threshold the XP is >= to wins.
  for (let lv = LEVEL_XP_THRESHOLDS.length; lv >= 1; lv--) {
    if (xp >= LEVEL_XP_THRESHOLDS[lv - 1]) return lv;
  }
  return 1;
}

/**
 * XP needed to reach the next level. Returns 0 when already at max.
 * Useful for the cover-page "Lv 4 · 320/500 XP" chip.
 */
export function xpToNextLevel(xp: number): number {
  const lv = computeSubjectLevel(xp);
  if (lv >= MAX_SUBJECT_LEVEL) return 0;
  return LEVEL_XP_THRESHOLDS[lv] - xp;
}

/**
 * Convenience: { level, xpInLevel, xpForNext } for UI rendering.
 * xpForNext is the XP threshold of the NEXT level (e.g. lv=3 → 300),
 * or LEVEL_XP_THRESHOLDS[MAX-1] (5000) when capped.
 */
export interface LevelDisplay {
  level: number;
  /** XP accumulated from level-start up to current xp. */
  xpInLevel: number;
  /** Threshold for the NEXT level (where the progress bar 100% lands). */
  xpForNext: number;
  /** Total XP this entity has. */
  xp: number;
  /** True when already at MAX_SUBJECT_LEVEL. */
  maxed: boolean;
}

export function describeLevel(xp: number): LevelDisplay {
  const safeXp = Number.isFinite(xp) && xp > 0 ? Math.floor(xp) : 0;
  const level = computeSubjectLevel(safeXp);
  const levelStart = LEVEL_XP_THRESHOLDS[level - 1];
  const maxed = level >= MAX_SUBJECT_LEVEL;
  const xpForNext = maxed
    ? LEVEL_XP_THRESHOLDS[MAX_SUBJECT_LEVEL - 1] // 5000 at the cap
    : LEVEL_XP_THRESHOLDS[level];
  return {
    level,
    xp: safeXp,
    xpInLevel: safeXp - levelStart,
    xpForNext,
    maxed,
  };
}

/**
 * Pure XP-delta calculator.
 *
 *   correct=false → 0 (K-3 鐵則: 答錯不扣分)
 *   correct=true  → floor( base[difficulty] × streakMultiplier(streakAfter) )
 *
 * `streakAfter` is the kid's streak AFTER counting THIS answer (i.e. for the
 * first correct of a session pass 1; for the 4th consecutive correct pass 4).
 *
 * `difficulty` is read from the question root field per
 * `docs/G3S2_ENRICHMENT_SPEC.md` ("difficulty is a root field, NEVER metadata").
 * Out-of-range / missing values fall back to d2 to keep the system honest
 * when题庫 metadata coverage is partial.
 */
export function computeXpDelta(
  correct: boolean,
  difficulty: number | undefined,
  streakAfter: number,
): number {
  if (!correct) return 0;
  const d = (difficulty === 1 || difficulty === 2 || difficulty === 3)
    ? difficulty
    : 2;
  const base = XP_PER_DIFFICULTY[d];
  const mult = streakMultiplier(streakAfter);
  return Math.floor(base * mult);
}

/** Map a streak count to its multiplier (highest matching threshold wins). */
export function streakMultiplier(streakAfter: number): number {
  if (!Number.isFinite(streakAfter) || streakAfter < 1) return 1.0;
  for (const tier of STREAK_BONUS) {
    if (streakAfter >= tier.minStreak) return tier.multiplier;
  }
  return 1.0;
}

/** Result of writing an XP delta into a PlayerProgress object. */
export interface XpApplyResult {
  /** The XP actually added (== delta input; 0 means no-op write). */
  delta: number;
  /** XP value AFTER write. */
  xpAfter: number;
  /** Level BEFORE write. */
  levelBefore: number;
  /** Level AFTER write. */
  levelAfter: number;
  /** True iff `levelAfter > levelBefore`. */
  leveledUp: boolean;
}

/** Internal: keep xpHistory bounded. Spec C3 will revisit; A keeps it small. */
const XP_HISTORY_CAP = 100;

/**
 * Mutate `progress` in place: add `delta` to subjectXP[subject], recompute
 * subjectLevel, append an xpHistory entry, and return a structured result so
 * the UI can render "+N XP" + the level-up overlay.
 *
 * Designed for Phase A's session loop: caller does
 *   const delta = computeXpDelta(correct, q.difficulty, streak);
 *   const r = applyXpDelta(progress, subject, delta, 'correct-d2');
 *   if (r.leveledUp) showCelebration(...);
 *   saveProgress(progress);
 *
 * Phase A intentionally does NOT touch progress.factionXP — see R-001
 * mitigation. Phase A also does NOT apply daily/per-question caps (those
 * are Phase C work per the spec); guarding is purely "delta >= 0".
 */
export function applyXpDelta(
  progress: PlayerProgress,
  subject: Subject,
  delta: number,
  reason: string,
  nowISO: string = new Date().toISOString(),
): XpApplyResult {
  // Lazy-init the maps so this is safe to call on partially-migrated fixtures.
  if (!progress.subjectXP) progress.subjectXP = {};
  if (!progress.subjectLevel) progress.subjectLevel = {};
  if (!Array.isArray(progress.xpHistory)) progress.xpHistory = [];

  const xpBefore = progress.subjectXP[subject] ?? 0;
  const levelBefore = progress.subjectLevel[subject] ?? computeSubjectLevel(xpBefore);

  // Negative or non-finite delta → no-op write (still returns "no change")
  // — prevents accidental decrement from a bad caller. 答錯不扣分 is enforced
  // upstream by `computeXpDelta` returning 0; we still allow delta=0 to flow
  // through (e.g. for audit "user attempted Q" hooks) but skip history then.
  const safeDelta = Number.isFinite(delta) && delta > 0 ? Math.floor(delta) : 0;
  const xpAfter = xpBefore + safeDelta;
  const levelAfter = computeSubjectLevel(xpAfter);

  progress.subjectXP[subject] = xpAfter;
  progress.subjectLevel[subject] = levelAfter;

  if (safeDelta > 0) {
    progress.xpHistory.push({
      date: nowISO.slice(0, 10),
      subject,
      delta: safeDelta,
      reason,
    });
    // FIFO trim — keep the most recent N entries.
    if (progress.xpHistory.length > XP_HISTORY_CAP) {
      progress.xpHistory.splice(0, progress.xpHistory.length - XP_HISTORY_CAP);
    }
  }

  return {
    delta: safeDelta,
    xpAfter,
    levelBefore,
    levelAfter,
    leveledUp: levelAfter > levelBefore,
  };
}

/**
 * Convenience for the answer-loop:
 *   addSubjectXP(progress, 'math', true, 2, 4)
 * returns the XpApplyResult and a friendly reason tag.
 *
 * Use this when you don't already have `delta` pre-computed.
 */
export function addSubjectXP(
  progress: PlayerProgress,
  subject: Subject,
  correct: boolean,
  difficulty: number | undefined,
  streakAfter: number,
  nowISO: string = new Date().toISOString(),
): XpApplyResult {
  const delta = computeXpDelta(correct, difficulty, streakAfter);
  const d = (difficulty === 1 || difficulty === 2 || difficulty === 3) ? difficulty : 2;
  const reason = correct ? `correct-d${d}` : 'incorrect';
  return applyXpDelta(progress, subject, delta, reason, nowISO);
}

// ── D-001 Phase C — daily caps + milestone push enqueue ─────────────────────
//
// Spec: SKILL_LEVEL_DESIGN.md §2.5 (防 grind 暴衝) + §3.3 (Lv5/Lv10 push reward).
//
// Three guards from §2.5:
//   1. 每題 XP cap = 20   (post-streak-multiplier)
//   2. 每日 XP cap = 500  (across subjects — boss prompt 同日 totalXP > 500 截斷)
//   3. 每日 level-up cap = 3 (XP 仍 accumulate 但 level 不再 bump)
//
// These functions are PURE-WRAPPER-style: they only enforce / record on top
// of the existing `applyXpDelta` primitive. The legacy `applyXpDelta` is
// intentionally NOT auto-capping so Phase A's existing test fixtures
// (`applyXpDelta(p, 'math', 50, ...)`) keep working unchanged. Callers in
// Phase C (learn-quiz commit hook) opt in via the new functions below.

/** Per-question XP cap (§2.5). Post-streak-multiplier value. */
export const PER_QUESTION_XP_CAP = 20;
/** Per-day XP cap (§2.5). Across all subjects (boss-prompt definition). */
export const DAILY_XP_CAP = 500;
/** Per-day level-up cap (§2.5). Across all subjects. */
export const DAILY_LEVELUP_CAP = 3;
/** Milestone levels that enqueue a parent push (§3.3). */
export const PUSH_MILESTONE_LEVELS: ReadonlyArray<5 | 10> = [5, 10];
/** Bounded queue size for pendingPushEvents to prevent runaway growth. */
const PUSH_QUEUE_CAP = 20;

export interface DailyCapResult {
  /** Final delta that should be passed to `applyXpDelta` (post-cap). */
  allowedDelta: number;
  /** True iff any cap (per-q or per-day) truncated the input delta. */
  capReached: boolean;
  /** Set when at least one cap clamp actually fired. */
  capReason?: 'per-question' | 'per-day' | 'both';
}

/** Normalise the cap counter to today, resetting on date roll-over. */
function ensureDailyCapState(
  progress: PlayerProgress,
  today: string,
): { date: string; xpToday: number; levelsToday: number } {
  if (!progress.dailyXpCap || progress.dailyXpCap.date !== today) {
    progress.dailyXpCap = { date: today, xpToday: 0, levelsToday: 0 };
  }
  return progress.dailyXpCap;
}

/**
 * Apply the per-question and per-day XP caps before the XP is actually
 * written. Mutates `progress.dailyXpCap` in place (resetting on day
 * roll-over) but does NOT write into subjectXP — caller wires the returned
 * `allowedDelta` into `applyXpDelta`.
 *
 * Cap semantics:
 *   • deltaXp clamped to `[0, PER_QUESTION_XP_CAP]` first (defensive: also
 *     handles negative/NaN input).
 *   • then clamped so `xpToday + delta <= DAILY_XP_CAP` — overflow becomes
 *     `capReached=true` and `allowedDelta` becomes the remaining headroom
 *     (possibly 0).
 *
 * Per the spec: 「超過 cap 仍 accumulate XP 但不 trigger level (避免異常數據)」
 * is enforced separately by `applyXpDeltaWithCaps` below (level-up cap, NOT
 * XP cap — XP itself IS truncated by the per-day cap, the level-cap only
 * prevents bumping subjectLevel beyond +3 today).
 */
export function applyDailyCap(
  progress: PlayerProgress,
  deltaXp: number,
  nowISO: string = new Date().toISOString(),
): DailyCapResult {
  const today = nowISO.slice(0, 10);
  const state = ensureDailyCapState(progress, today);

  // Step 1: per-question clamp + defensive sanitise.
  const raw = Number.isFinite(deltaXp) && deltaXp > 0 ? Math.floor(deltaXp) : 0;
  const perQClamped = Math.min(raw, PER_QUESTION_XP_CAP);
  const perQHit = perQClamped < raw;

  // Step 2: per-day clamp.
  const dailyHeadroom = Math.max(0, DAILY_XP_CAP - state.xpToday);
  const allowedDelta = Math.min(perQClamped, dailyHeadroom);
  const perDayHit = allowedDelta < perQClamped;

  // Step 3: record the awarded amount onto today's counter.
  state.xpToday += allowedDelta;

  const capReached = perQHit || perDayHit;
  let capReason: DailyCapResult['capReason'];
  if (perQHit && perDayHit) capReason = 'both';
  else if (perQHit) capReason = 'per-question';
  else if (perDayHit) capReason = 'per-day';

  return { allowedDelta, capReached, capReason };
}

/**
 * Enqueue a `level-up-milestone` push event when crossing Lv5 or Lv10.
 *
 * Idempotent: if `levelBefore >= milestone` already, no enqueue. Multiple
 * milestones crossed in one delta (e.g. cheat-mode 0→5000 XP) enqueue both.
 *
 * Spec D-001 §3.3 + D-005 §3.2 — only the two boss-approved milestones push
 * (kids spam parents otherwise; sane defaults beat opt-in fatigue).
 */
export function enqueueMilestonePushIfNeeded(
  progress: PlayerProgress,
  subject: Subject,
  levelBefore: number,
  levelAfter: number,
  nowISO: string = new Date().toISOString(),
): Array<5 | 10> {
  if (levelAfter <= levelBefore) return [];
  if (!Array.isArray(progress.pendingPushEvents)) progress.pendingPushEvents = [];
  const enqueued: Array<5 | 10> = [];
  for (const m of PUSH_MILESTONE_LEVELS) {
    if (levelBefore < m && levelAfter >= m) {
      progress.pendingPushEvents.push({
        type: 'level-up-milestone',
        subject,
        level: m,
        timestamp: nowISO,
      });
      enqueued.push(m);
    }
  }
  // Bounded queue — drop oldest if past cap.
  if (progress.pendingPushEvents.length > PUSH_QUEUE_CAP) {
    progress.pendingPushEvents.splice(
      0,
      progress.pendingPushEvents.length - PUSH_QUEUE_CAP,
    );
  }
  return enqueued;
}

/** Composite result merging Phase A apply + Phase C cap/push outcomes. */
export interface XpApplyWithCapsResult extends XpApplyResult {
  /** Pre-cap requested delta (== input). */
  requestedDelta: number;
  /** True iff cap clamped the delta (per-q or per-day). */
  capReached: boolean;
  capReason?: DailyCapResult['capReason'];
  /** True iff today's level-up cap (3) blocked an otherwise-due level bump. */
  levelCapBlocked: boolean;
  /** Milestone levels enqueued during THIS write (subset of [5,10]). */
  pushEnqueued: Array<5 | 10>;
}

/**
 * Phase C answer-loop entry point.
 *
 * Wraps `applyXpDelta` with:
 *   1. `applyDailyCap` (per-q 20 + per-day 500 ceiling)
 *   2. `enqueueMilestonePushIfNeeded` (Lv5/Lv10 push)
 *   3. daily level-up cap (3): XP still credited, but if the cap is hit the
 *      subjectLevel field is rolled BACK to its pre-delta value so the kid's
 *      段位 doesn't bump beyond +3 today. The xpHistory entry is preserved
 *      (audit trail keeps full granularity).
 *
 * The previous level-up cap behaviour ("XP 仍 accumulate 但 level 不變") is
 * implemented by post-rollback of `progress.subjectLevel[subject]`. Tomorrow's
 * first answer naturally recomputes the correct level via `computeSubjectLevel`
 * when xpAfter is fed back through `applyXpDelta` (or any reader call).
 *
 * Note re: level cap rollback: this means `progress.subjectXP[subject]` and
 * `progress.subjectLevel[subject]` can be momentarily desynced (XP at 600,
 * stored Lv stale at 5 when 6 would be due). All UI readers MUST go through
 * `computeSubjectLevel(progress.subjectXP[subject])` to get the AUTHORITATIVE
 * level — never trust the stored field cold. The rollback is purely a "stop
 * the celebratory animation from firing tomorrow's level up today" mechanism.
 */
export function applyXpDeltaWithCaps(
  progress: PlayerProgress,
  subject: Subject,
  deltaXp: number,
  reason: string,
  nowISO: string = new Date().toISOString(),
): XpApplyWithCapsResult {
  const requestedDelta = Number.isFinite(deltaXp) && deltaXp > 0 ? Math.floor(deltaXp) : 0;
  const cap = applyDailyCap(progress, requestedDelta, nowISO);
  const applied = applyXpDelta(progress, subject, cap.allowedDelta, reason, nowISO);

  // Daily level-up cap (§2.5 #3): if today already booked 3 level-ups, hold
  // the player at their pre-delta level even though XP DID accrue.
  let levelCapBlocked = false;
  if (applied.leveledUp) {
    const state = ensureDailyCapState(progress, nowISO.slice(0, 10));
    if (state.levelsToday >= DAILY_LEVELUP_CAP) {
      // Rollback — keep XP, hold level. Touch subjectLevel only.
      progress.subjectLevel = progress.subjectLevel ?? {};
      progress.subjectLevel[subject] = applied.levelBefore;
      levelCapBlocked = true;
    } else {
      // Count THIS level-up (or run of level-ups if multi-tier jump in one
      // delta) toward today's quota.
      state.levelsToday += (applied.levelAfter - applied.levelBefore);
    }
  }

  // Push enqueue: only fire when the player's STORED level actually advanced
  // (i.e. level cap did NOT roll us back). Otherwise we'd enqueue a Lv5/Lv10
  // milestone that the kid hasn't visibly hit yet.
  const effectiveLevelAfter = levelCapBlocked ? applied.levelBefore : applied.levelAfter;
  const pushEnqueued = enqueueMilestonePushIfNeeded(
    progress,
    subject,
    applied.levelBefore,
    effectiveLevelAfter,
    nowISO,
  );

  return {
    ...applied,
    leveledUp: applied.leveledUp && !levelCapBlocked,
    levelAfter: effectiveLevelAfter,
    requestedDelta,
    capReached: cap.capReached,
    capReason: cap.capReason,
    levelCapBlocked,
    pushEnqueued,
  };
}
